/**
 * offlineQueue.js — minimal IndexedDB-backed queue for failed mutations.
 *
 * When axios reports a network error on a non-GET request we push the
 * essential request shape (method/url/data/headers) into this queue.
 * On `window.online` we drain the queue in FIFO order, replaying each
 * request once. Successful replays toast a "delivered" message.
 *
 * Limitations:
 * - Best-effort: a request that 4xx's after going online (server changed
 *   schema, body now invalid) is dropped from the queue with an error toast.
 * - No conflict resolution; concurrent edits between offline + online
 *   clients are last-write-wins.
 */

// ── What may be queued [AUDIT-2026-10-20 OFFLINE-REPLAY] ─────────────────
// A network error does not mean the request never arrived: a dropped
// connection, a proxy reset or a CORS failure all reach axios as ERR_NETWORK
// while the server may already have written. Every failed mutation used to be
// queued and replayed later, so a fee, a salary or an expense whose response
// was lost was created a second time on replay (and again if the user had
// pressed "Save" once more in the meantime). Only requests that are safe to
// send twice are queued while online:
//   - PUT / PATCH / DELETE (the finance PUT carries expected_paid_amount, so
//     a replayed payment edit is a no-op);
//   - POSTs carrying their own idempotency key (bulk payments, class fees,
//     payroll, single payments);
//   - attendance, which the server records once per student and day.
// Any other POST is queued only when the browser is OFFLINE, i.e. it never
// left the device.
const REPLAY_SAFE_POST = [/^\/?attendance(\/bulk)?\/?$/];

const parseBody = (data) => {
  if (!data) return null;
  if (typeof data === 'object') return data;
  try { return JSON.parse(data); } catch { return null; }
};

export const isReplaySafe = (config = {}, online = true) => {
  const method = String(config.method || 'get').toLowerCase();
  if (['put', 'patch', 'delete'].includes(method)) return true;
  if (method !== 'post') return false;
  if (!online) return true;
  const body = parseBody(config.data);
  if (body && !Array.isArray(body) && (body.batch_key || body.idempotency_key)) return true;
  const path = String(config.url || '').split('?')[0];
  return REPLAY_SAFE_POST.some((re) => re.test(path));
};

/** The part of an axios config the queue stores. Never the Authorization
 * header: a replay is sent with the token of whoever is signed in THEN, and
 * only when that is the same account that queued it (`user_id`). */
export const queueableRequest = (config = {}, userId = null) => {
  const headers = {};
  Object.entries(config.headers || {}).forEach(([k, v]) => {
    if (k.toLowerCase() === 'authorization') return;
    if (typeof v === 'string' || typeof v === 'number') headers[k] = v;
  });
  return {
    method: String(config.method || 'get').toLowerCase(),
    url: config.url,
    baseURL: config.baseURL,
    data: config.data,
    params: config.params,
    headers,
    user_id: userId,
  };
};

const DB_NAME = 'edugete-offline';
const DB_VERSION = 1;
const STORE = 'queue';

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (mode = 'readonly') => {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
};

export const enqueue = async (item) => {
  try {
    const store = await tx('readwrite');
    return await new Promise((resolve, reject) => {
      const req = store.add({
        ...item,
        queued_at: new Date().toISOString(),
      });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    // IndexedDB unavailable (private mode, etc.) — fail soft.
    console.warn('[offlineQueue] enqueue failed', err);
    return null;
  }
};

export const all = async () => {
  try {
    const store = await tx('readonly');
    return await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
};

export const remove = async (id) => {
  try {
    const store = await tx('readwrite');
    return await new Promise((resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    /* ignore */
  }
};

export const size = async () => (await all()).length;
